import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button, Select } from '../atoms';
import { StatCard, StatCardRow, NotificationTypeIcon } from '../organisms';
import { usePermissions } from '../lib/usePermissions';
import {
  Notification,
  PORTAL_TRIGGERS,
  urgencyVariant,
  urgencyLabel,
  metaFor,
  humanize,
  isUnread,
  routeFor,
  relativeTime,
} from '../lib/notifications';

/**
 * Notifications — /v2/notifications
 *
 * The full self-scoped feed over the last 90 days (GET /api/my-notifications?days=90),
 * newest-first, with an urgency pill, title + body, an entity deep-link, and a
 * relative timestamp. A trigger_type filter narrows the feed; per-row mark-read /
 * dismiss + a "Mark all read" action clear the unread state.
 *
 * Row shape + the read rule + the per-trigger icon/label + the deep-link map all
 * come from lib/notifications (shared with the header bell + dashboard surfaces).
 */
export function Notifications() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canEditNotificationSettings = permissions.data?.user.role === 'super_admin';
  const queryClient = useQueryClient();
  const [triggerFilter, setTriggerFilter] = useState('');

  // Self-scoped feed over the last 90 days. Rows carry the caller's own
  // read/read_at flags (notification_recipients JOIN in src/routes/enriched.js).
  const query = useQuery<Notification[]>({
    queryKey: ['notifications-list', { days: 90, limit: 200 }],
    queryFn: ({ signal }) => api.get<Notification[]>('/api/my-notifications?days=90&limit=200', signal),
  });

  function invalidateAll() {
    // Refresh the page feed AND the bell/dashboard slices (all read the same feed).
    queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-bell'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-dashboard'] });
  }

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/api/notifications/${id}/read`),
    onSuccess: invalidateAll,
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => api.patch(`/api/notifications/${id}/dismiss`),
    onSuccess: invalidateAll,
  });

  const markAll = useMutation({
    mutationFn: () => api.post('/api/notifications/read-all'),
    onSuccess: invalidateAll,
  });

  const all = query.data ?? [];

  const sorted = useMemo(
    () => [...all].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')),
    [all],
  );

  const triggerOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const n of all) if (n.trigger_type) seen.add(n.trigger_type);
    return Array.from(seen)
      .sort()
      .map((t) => ({ value: t, label: metaFor(t).label }));
  }, [all]);

  const visible = useMemo(
    () => (triggerFilter ? sorted.filter((n) => n.trigger_type === triggerFilter) : sorted),
    [sorted, triggerFilter],
  );

  const total = all.length;
  const unread = all.filter((n) => isUnread(n)).length;
  const portalEvents = all.filter((n) => n.trigger_type && PORTAL_TRIGGERS.has(n.trigger_type)).length;

  return (
    <>
      <header style={{
        marginBottom: 'var(--sp-5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 'var(--sp-3)',
        flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: 'var(--fs-3xl)',
            fontWeight: 'var(--fw-semibold)',
            letterSpacing: 'var(--ls-tight)',
            color: 'var(--color-ink)',
          }}>Notifications</h1>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-md)',
          }}>
            {query.isLoading ? 'Loading…' : `${total} notification${total === 1 ? '' : 's'} · last 90 days`}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexShrink: 0 }}>
          {canEditNotificationSettings && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/org-settings')}>
              Edit notifications
            </Button>
          )}
          {unread > 0 && (
            <Button variant="secondary" size="sm" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              {markAll.isPending ? 'Marking…' : `Mark all read (${unread})`}
            </Button>
          )}
        </div>
      </header>

      <StatCardRow style={{ marginBottom: 'var(--sp-5)' }}>
        <StatCard label="Total" value={total} accent="hunter" />
        <StatCard label="Unread" value={unread} accent={unread > 0 ? 'amber' : 'neutral'} caption="Not yet cleared" />
        <StatCard label="Portal events" value={portalEvents} accent="info" caption="Client views, uploads + comments" />
      </StatCardRow>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        marginBottom: 'var(--sp-4)',
        flexWrap: 'wrap',
      }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Type</span>
          <div style={{ minWidth: 260 }}>
            <Select
              size="sm"
              placeholder="All types"
              options={triggerOptions}
              value={triggerFilter}
              onChange={(e) => setTriggerFilter(e.target.value)}
            />
          </div>
        </label>
        {triggerFilter && (
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            {visible.length} match{visible.length === 1 ? '' : 'es'}
          </span>
        )}
      </div>

      {query.error && (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
          marginBottom: 'var(--sp-4)',
        }}>
          <strong>Failed to load notifications:</strong> {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && !query.error && visible.length === 0 && (
        <div style={{
          padding: 'var(--sp-8)',
          background: 'var(--color-card)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)',
          color: 'var(--color-muted)',
          textAlign: 'center',
        }}>
          {total === 0 ? 'No notifications yet.' : 'No notifications match this filter.'}
        </div>
      )}

      {visible.length > 0 && (
        <div style={{
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--sh-sm)',
          overflow: 'hidden',
        }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {visible.map((n) => {
              const meta = metaFor(n.trigger_type);
              const isPortal = !!n.trigger_type && PORTAL_TRIGGERS.has(n.trigger_type);
              const route = routeFor(n);
              const linkable = !!route;
              const urgency = (n.urgency ?? 'normal') as string;
              return (
                // Mouse users click anywhere on the row; the keyboard way in is the
                // title button below, whose click bubbles up to this handler.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                <li
                  key={n.id}
                  onClick={() => {
                    if (isUnread(n)) markRead.mutate(n.id);
                    if (route) navigate(route);
                  }}
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-3)',
                    padding: 'var(--sp-4)',
                    borderBottom: '1px solid var(--color-border)',
                    cursor: linkable ? 'pointer' : 'default',
                    background: isUnread(n) ? 'var(--brand-primary-bg)' : undefined,
                    transition: 'background 120ms ease',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      width: 28,
                      color: 'var(--color-ink-3)',
                    }}
                  ><NotificationTypeIcon type={n.trigger_type} size={18} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                      flexWrap: 'wrap',
                      marginBottom: 'var(--sp-1)',
                    }}>
                      <button type="button" style={{
                        font: 'inherit',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        textAlign: 'left',
                        cursor: linkable ? 'pointer' : 'default',
                        fontSize: 'var(--fs-md)',
                        fontWeight: 'var(--fw-semibold)',
                        color: 'var(--color-ink)',
                      }}>{n.title || meta.label}</button>
                      <Badge variant={urgencyVariant[urgency] ?? 'neutral'}>
                        {urgencyLabel[urgency] ?? humanize(urgency)}
                      </Badge>
                      {isPortal && <Badge variant="info" dot>Portal</Badge>}
                    </div>
                    {n.body && (
                      <div style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--color-ink-3)',
                        marginBottom: 'var(--sp-1)',
                      }}>{n.body}</div>
                    )}
                    <div style={{
                      display: 'flex',
                      gap: 'var(--sp-2)',
                      alignItems: 'center',
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--color-muted)',
                      fontFamily: 'var(--font-mono)',
                    }}>
                      <span>{meta.label}</span>
                      {linkable && (
                        <>
                          <span>·</span>
                          <span>{n.entity_type} → open</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 'var(--sp-1)',
                    flexShrink: 0,
                  }}>
                    <span style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--color-muted)',
                      whiteSpace: 'nowrap',
                    }}>{relativeTime(n.created_at)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); dismiss.mutate(n.id); }}
                    >
                      Dismiss
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}
